/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * System Chapter Content (Layer 2)
 *
 * Full-depth content for each system type, containing the same substance
 * as the system pages in the full PDF decision pack, but structured
 * for interactive presentation.
 *
 * Each chapter includes:
 * - System layout diagram data
 * - Behavioral archetype and description
 * - Comfort-over-time curve data
 * - Hot water capability and limitations
 * - "Works best when" guidance
 * - "Things to be aware of" caveats
 */

export interface ComfortCurvePoint {
  hour: number;
  comfort: number;
}

export interface SystemChapterData {
  /** System display name */
  title: string;
  /** Behavioral archetype */
  archetype: {
    type: 'RESPONSIVE_BURST' | 'STEADY_CONTINUOUS' | 'LEGACY_CONSTRAINED';
    icon: string;
    label: string;
    summary: string;
  };
  /** System layout diagram data */
  layout: {
    title: string;
    components: Array<{
      icon: 'boiler' | 'cylinder' | 'heat_pump' | 'tank_fe' | 'tank_cws';
      label: string;
    }>;
    tankNote: string;
  };
  /** Comfort curve data */
  comfort: {
    curve: ComfortCurvePoint[];
    bullets: string[];
  };
  /** Hot water capability */
  hotWater: {
    summary: string;
    details: string[];
    limitations: string[];
  };
  /** Works best when */
  worksBestWhen: string[];
  /** Things to be aware of */
  caveats: string[];
}

// Gas boiler comfort curve - fast warm-up, stable operation
const gasBoilerComfortCurve: ComfortCurvePoint[] = [
  { hour: 0, comfort: 30 },
  { hour: 0.5, comfort: 50 },
  { hour: 1, comfort: 68 },
  { hour: 1.5, comfort: 78 },
  { hour: 2, comfort: 85 },
  { hour: 3, comfort: 90 },
  { hour: 4, comfort: 92 },
  { hour: 6, comfort: 93 },
  { hour: 8, comfort: 93 },
  { hour: 12, comfort: 93 },
  { hour: 16, comfort: 93 },
  { hour: 20, comfort: 93 },
  { hour: 24, comfort: 93 },
];

// Heat pump comfort curve - slow, steady warm-up
const heatPumpComfortCurve: ComfortCurvePoint[] = [
  { hour: 0, comfort: 30 },
  { hour: 1, comfort: 38 },
  { hour: 2, comfort: 48 },
  { hour: 3, comfort: 58 },
  { hour: 4, comfort: 66 },
  { hour: 5, comfort: 73 },
  { hour: 6, comfort: 79 },
  { hour: 7, comfort: 84 },
  { hour: 8, comfort: 88 },
  { hour: 10, comfort: 92 },
  { hour: 12, comfort: 94 },
  { hour: 16, comfort: 95 },
  { hour: 20, comfort: 96 },
  { hour: 24, comfort: 96 },
];

export const systemChapterContent: Record<string, SystemChapterData> = {
  // Combi Boiler
  combi: {
    title: 'Combi Boiler',
    archetype: {
      type: 'RESPONSIVE_BURST',
      icon: '⚡',
      label: 'Responsive & Burst-Ready',
      summary: 'Fast warm-up, flexible routines, handles peak demands easily. Heats water on demand with no storage tank required.',
    },
    layout: {
      title: 'Combi Boiler System',
      components: [
        { icon: 'boiler', label: 'Wall-mounted combi boiler (heats water on demand)' },
      ],
      tankNote: 'No loft tanks required (mains-fed)',
    },
    comfort: {
      curve: gasBoilerComfortCurve,
      bullets: [
        'Fast warm-up from cold — typically reaches comfort within 30–60 minutes',
        'Stable, consistent temperature once the home is warm',
        'Flexible for changing routines — responds quickly to thermostat changes',
      ],
    },
    hotWater: {
      summary: 'On-demand hot water, one outlet at full flow',
      details: [
        'Heats water instantly as you use it — no waiting for a tank to heat',
        'Unlimited hot water duration (no tank to run out)',
        'Typical flow rate: 10–13 litres per minute at 35°C rise',
        'Best suited to one hot water outlet at a time',
      ],
      limitations: [
        'Flow rate drops when multiple taps are used simultaneously',
        'Hot water temperature depends on mains pressure and incoming water temperature',
        'Not ideal for filling large baths quickly in hard water areas',
        'No stored hot water during boiler maintenance or breakdown',
      ],
    },
    worksBestWhen: [
      'The home has 1–2 bathrooms with modest hot water demand',
      'Space is limited and a cylinder would be difficult to accommodate',
      'Hot water is typically used one outlet at a time',
      'Heating patterns are varied (mornings, evenings, different days)',
      'Quick response to thermostat changes is valued',
    ],
    caveats: [
      'Multiple simultaneous hot water uses will reduce flow and temperature',
      'In hard water areas, the heat exchanger may scale up faster — consider a water softener',
      'If mains pressure is low, hot water performance will be affected',
      'The boiler works harder than stored systems, which may affect longevity in high-demand homes',
    ],
  },

  // System Boiler with Unvented Cylinder
  system_cylinder: {
    title: 'System Boiler with Unvented Cylinder',
    archetype: {
      type: 'RESPONSIVE_BURST',
      icon: '⚡',
      label: 'Responsive & Burst-Ready',
      summary: 'Fast warm-up with stored hot water for multiple outlets. Combines the responsiveness of a modern boiler with the capacity of stored water.',
    },
    layout: {
      title: 'System Boiler + Unvented Cylinder',
      components: [
        { icon: 'boiler', label: 'System boiler' },
        { icon: 'cylinder', label: 'Unvented cylinder' },
      ],
      tankNote: 'No loft tanks required (mains-fed, sealed system)',
    },
    comfort: {
      curve: gasBoilerComfortCurve,
      bullets: [
        'Fast warm-up from cold — heating responds quickly to demand',
        'Stable comfort throughout the day with good controls',
        'Hot water available at mains pressure to multiple outlets',
      ],
    },
    hotWater: {
      summary: 'Stored hot water at mains pressure, 2+ outlets simultaneously',
      details: [
        'Hot water stored in an insulated cylinder (typically 150–250 litres)',
        'Mains pressure delivery — strong shower flow without pumps',
        'Multiple taps and showers can run simultaneously',
        'Recovery time depends on cylinder size and boiler output',
      ],
      limitations: [
        'Requires space for a cylinder (usually in an airing cupboard)',
        'Heavy use can temporarily deplete the cylinder — recovery takes 20–45 minutes',
        'Some standby heat loss from the cylinder (mitigated by good insulation)',
        'Annual inspection required for unvented cylinders',
      ],
    },
    worksBestWhen: [
      'The home has 2+ bathrooms with regular simultaneous hot water demand',
      'Strong shower pressure is important without needing pumps',
      'There is space for a hot water cylinder',
      'Heating patterns are varied and quick response is valued',
      'The household prefers reliable hot water even during peak demand',
    ],
    caveats: [
      'Cylinder requires dedicated space — typically a cupboard',
      'Unvented systems require annual safety inspection',
      'If the cylinder is undersized, recovery periods may feel limiting',
      'Initial installation cost is higher than combi systems',
    ],
  },

  // Regular Boiler with Vented Cylinder
  regular_vented: {
    title: 'Regular Boiler with Vented System',
    archetype: {
      type: 'LEGACY_CONSTRAINED',
      icon: '🔧',
      label: 'Conventional & Tank-Limited',
      summary: 'Traditional setup with loft tanks and gravity-fed hot water. Reliable and familiar, but limited by tank capacity and water pressure.',
    },
    layout: {
      title: 'Regular Boiler + Vented System',
      components: [
        { icon: 'boiler', label: 'Regular boiler' },
        { icon: 'cylinder', label: 'Hot water cylinder' },
        { icon: 'tank_fe', label: 'F&E tank (~25L)' },
        { icon: 'tank_cws', label: 'CWS tank (~227L)' },
      ],
      tankNote: 'Loft tanks: F&E + CWS (traditional open-vented system)',
    },
    comfort: {
      curve: gasBoilerComfortCurve,
      bullets: [
        'Familiar, traditional heating behaviour',
        'Hot water temperature is very stable once the cylinder is heated',
        'Works with existing radiator systems without modification',
      ],
    },
    hotWater: {
      summary: 'Gravity-fed stored hot water, limited pressure',
      details: [
        'Hot water stored in a cylinder and fed by gravity from the loft tank',
        'Hot water pressure depends on the height of the cold water tank',
        'Shower pumps often required for acceptable shower performance',
        'Traditional, well-understood system with simple maintenance',
      ],
      limitations: [
        'Lower water pressure than mains-fed systems — showers may need pumps',
        'Requires loft space for two tanks (F&E and CWS)',
        'Loft tanks can freeze in very cold weather if not insulated',
        'Limited hot water flow rate without pumping',
      ],
    },
    worksBestWhen: [
      'The home already has a vented system and upgrading to unvented is not practical',
      'Loft space is available for the required tanks',
      'Shower pumps are already installed or acceptable',
      'A simple, traditional system is preferred over modern sealed systems',
      'The household is comfortable with the existing hot water experience',
    ],
    caveats: [
      'Lower hot water pressure than modern systems — consider pumps or upgrade path',
      'Loft tanks require insulation and occasional inspection',
      'System is more complex with more components to maintain',
      'Not compatible with some modern energy-efficient upgrades',
    ],
  },

  // Regular Boiler with Unvented Cylinder (sealed heating, mains-pressure hot water)
  regular_unvented: {
    title: 'Regular Boiler with Unvented Cylinder',
    archetype: {
      type: 'RESPONSIVE_BURST',
      icon: '⚡',
      label: 'Responsive & Burst-Ready',
      summary: 'Modern sealed heating system with mains-pressure hot water. Combines the reliability of a regular boiler with the performance of an unvented cylinder.',
    },
    layout: {
      title: 'Regular Boiler + Unvented Cylinder (Sealed)',
      components: [
        { icon: 'boiler', label: 'Regular boiler' },
        { icon: 'cylinder', label: 'Unvented cylinder' },
        { icon: 'tank_fe', label: 'F&E tank (~15L)' },
      ],
      tankNote: 'Loft tanks: F&E only (sealed primary, mains-fed DHW)',
    },
    comfort: {
      curve: gasBoilerComfortCurve,
      bullets: [
        'Fast warm-up from cold — responsive to heating demand',
        'Stable comfort with good controls',
        'Hot water at mains pressure without pumps',
      ],
    },
    hotWater: {
      summary: 'Stored hot water at mains pressure, 2+ outlets simultaneously',
      details: [
        'Hot water stored in an insulated unvented cylinder',
        'Mains pressure delivery — strong showers without pumps',
        'Multiple outlets can run simultaneously',
        'Recovery time depends on cylinder size and boiler output',
      ],
      limitations: [
        'Requires space for a cylinder',
        'Still requires a small F&E tank in the loft for the heating circuit',
        'Annual inspection required for the unvented cylinder',
        'Higher installation complexity than simpler systems',
      ],
    },
    worksBestWhen: [
      'You want mains-pressure hot water but prefer a traditional boiler setup',
      'The heating circuit works well with a regular boiler',
      'Space is available for both cylinder and small loft tank',
      'Multiple bathrooms require simultaneous hot water at good pressure',
    ],
    caveats: [
      'More components than a system boiler + unvented setup',
      'F&E tank still required in loft (small, but needs access)',
      'Annual cylinder inspection adds maintenance requirement',
      'Consider whether a system boiler might be simpler for your situation',
    ],
  },

  // Air Source Heat Pump
  heat_pump: {
    title: 'Air Source Heat Pump System',
    archetype: {
      type: 'STEADY_CONTINUOUS',
      icon: '🌊',
      label: 'Steady & Continuous',
      summary: 'Slow, steady warm-up with very stable comfort once warm. Best suited to homes that maintain a constant temperature rather than heating in bursts.',
    },
    layout: {
      title: 'Air Source Heat Pump System',
      components: [
        { icon: 'heat_pump', label: 'Heat pump unit (external)' },
        { icon: 'cylinder', label: 'Hot water cylinder' },
      ],
      tankNote: 'No loft tanks required (sealed system)',
    },
    comfort: {
      curve: heatPumpComfortCurve,
      bullets: [
        'Slow, steady warm-up from cold — plan for longer preheat times',
        'Very stable and consistent comfort once the home is up to temperature',
        'Best operated continuously or on predictable schedules',
      ],
    },
    hotWater: {
      summary: 'Stored hot water, heated by the heat pump',
      details: [
        'Hot water stored in a dedicated cylinder (typically 200–300 litres)',
        'Heat pump heats water to 45–55°C, with periodic legionella cycles',
        'Multiple outlets can run simultaneously',
        'Recovery time is longer than gas systems — larger cylinders recommended',
      ],
      limitations: [
        'Hot water heating takes longer than gas boilers',
        'Cylinder must be larger to compensate for slower heating',
        'Legionella protection cycles use an immersion heater backup',
        'Not suited to unpredictable, high-demand hot water use',
      ],
    },
    worksBestWhen: [
      'The home is occupied during the day and can maintain steady temperatures',
      'Insulation levels are good to moderate (reduces heat loss)',
      'Heating schedules are predictable and planned in advance',
      'Running costs and environmental impact are priorities',
      'Radiators are oversized or underfloor heating is present',
    ],
    caveats: [
      'Slower response to temperature changes — not ideal for burst heating',
      'Requires outdoor space for the external unit',
      'Works best with larger radiators or underfloor heating',
      'Hot water recovery is slower than gas systems',
      'Running costs depend heavily on electricity tariff',
      'Noise from outdoor unit should be considered for placement',
    ],
  },

  // Ground Source Heat Pump
  heat_pump_ground: {
    title: 'Ground Source Heat Pump System',
    archetype: {
      type: 'STEADY_CONTINUOUS',
      icon: '🌊',
      label: 'Steady & Continuous',
      summary: 'Very steady comfort with consistent heat output. Ground loops provide stable source temperatures for efficient, continuous operation.',
    },
    layout: {
      title: 'Ground Source Heat Pump System',
      components: [
        { icon: 'heat_pump', label: 'Heat pump unit (indoor plant room)' },
        { icon: 'cylinder', label: 'Hot water cylinder' },
      ],
      tankNote: 'Requires ground loop installation (horizontal trenches or boreholes)',
    },
    comfort: {
      curve: heatPumpComfortCurve,
      bullets: [
        'Slow, steady warm-up from cold — plan for longer preheat times',
        'Very stable and consistent comfort once the home is up to temperature',
        'Best operated continuously or on predictable schedules',
      ],
    },
    hotWater: {
      summary: 'Stored hot water, heated by the ground source heat pump',
      details: [
        'Hot water stored in a dedicated cylinder (typically 200–300 litres)',
        'Ground source heat pumps deliver consistent temperatures year-round',
        'Multiple outlets can run simultaneously',
        'Recovery time is longer than gas systems — larger cylinders recommended',
      ],
      limitations: [
        'Hot water heating takes longer than gas boilers',
        'Cylinder must be larger to compensate for slower heating',
        'Legionella protection cycles use an immersion heater backup',
        'Not suited to unpredictable, high-demand hot water use',
      ],
    },
    worksBestWhen: [
      'You have outdoor land for ground loops or boreholes',
      'The home is occupied during the day and can maintain steady temperatures',
      'Insulation levels are good to moderate (reduces heat loss)',
      'Running costs and environmental impact are priorities',
      'Radiators are oversized or underfloor heating is present',
    ],
    caveats: [
      'Slower response to temperature changes — not ideal for burst heating',
      'Ground loop installation adds disruption and upfront cost',
      'Works best with larger radiators or underfloor heating',
      'Hot water recovery is slower than gas systems',
      'Running costs depend heavily on electricity tariff',
      'Ground loop design must be engineered for long-term performance',
    ],
  },

  // Hybrid System (heat pump + gas boiler backup)
  hybrid: {
    title: 'Hybrid Heating System',
    archetype: {
      type: 'RESPONSIVE_BURST',
      icon: '⚡',
      label: 'Responsive & Burst-Ready',
      summary: 'Combines heat pump efficiency with gas boiler responsiveness. Uses the heat pump for baseline heating and switches to gas for peak demand or very cold weather.',
    },
    layout: {
      title: 'Hybrid Heat Pump + Gas Boiler',
      components: [
        { icon: 'heat_pump', label: 'Heat pump (external)' },
        { icon: 'boiler', label: 'Gas boiler (backup)' },
        { icon: 'cylinder', label: 'Hot water cylinder' },
      ],
      tankNote: 'No loft tanks required (sealed system)',
    },
    comfort: {
      curve: gasBoilerComfortCurve,
      bullets: [
        'Heat pump provides efficient baseline heating',
        'Gas boiler kicks in for fast warm-up and peak demand',
        'Flexible operation depending on conditions and energy costs',
      ],
    },
    hotWater: {
      summary: 'Stored hot water, heated by either heat pump or boiler',
      details: [
        'Hot water cylinder heated primarily by heat pump',
        'Gas boiler provides backup for high demand or quick recovery',
        'Multiple outlets at mains pressure',
        'More flexible than heat-pump-only systems',
      ],
      limitations: [
        'More complex system with two heat sources',
        'Requires outdoor space for heat pump unit',
        'Higher initial installation cost',
        'System controls must be properly configured for efficiency',
      ],
    },
    worksBestWhen: [
      'You want heat pump efficiency but need fast response capability',
      'The home has varying occupancy patterns',
      'Gas is available and you want a backup for very cold days',
      'You are transitioning towards lower-carbon heating gradually',
    ],
    caveats: [
      'More components means more potential maintenance',
      'System must be properly commissioned to balance efficiency and comfort',
      'Running costs depend on how often the gas boiler is used',
      'Consider whether pure heat pump or pure gas might be simpler',
    ],
  },
};

/**
 * Get system chapter content by option data
 * Maps from recommendation option to appropriate chapter
 */
export function getSystemChapterForOption(
  boiler?: string,
  water?: string,
  systemType?: string
): SystemChapterData | null {
  const chapterId = getSystemChapterIdForOption(boiler, water, systemType);
  return chapterId ? systemChapterContent[chapterId] : null;
}

/**
 * Resolve the system chapter ID for a recommendation option.
 */
export function getSystemChapterIdForOption(
  boiler?: string,
  water?: string,
  systemType?: string
): keyof typeof systemChapterContent | null {
  const boilerLower = boiler?.toLowerCase() || '';
  const waterLower = water?.toLowerCase() || '';
  const systemTypeLower = systemType?.toLowerCase() || '';

  // Heat pump
  if (
    systemTypeLower.includes('heat_pump') ||
    boilerLower.includes('heat_pump') ||
    boilerLower.includes('ashp') ||
    boilerLower.includes('gshp') ||
    boilerLower.includes('ground')
  ) {
    if (boilerLower.includes('hybrid')) {
      return 'hybrid';
    }
    if (boilerLower.includes('gshp') || boilerLower.includes('ground')) {
      return 'heat_pump_ground';
    }
    if (boilerLower.includes('ashp') || boilerLower.includes('air')) {
      return 'heat_pump';
    }
    return null;
  }

  // Combi
  if (boilerLower.includes('combi')) {
    return 'combi';
  }

  // System boiler
  if (boilerLower.includes('system')) {
    return 'system_cylinder';
  }

  // Regular boiler
  if (boilerLower.includes('regular')) {
    const isUnvented = waterLower.includes('unvented');
    return isUnvented ? 'regular_unvented' : 'regular_vented';
  }

  // Fallback based on water type
  if (waterLower.includes('unvented')) {
    return 'system_cylinder';
  }

  return null;
}
